import { canApplyPwaUpdate, reservePwaUpdate } from './update-lock';

export const PWA_UPDATE_PROTOCOL = 1;
export const PWA_UPDATE_MAX_DURATION_MS = 10_000;
export type UpdateReadiness = 'ready' | 'busy' | 'unverified';

interface CoordinationRequest {
  readonly type: 'EVENT_UPDATE_COORDINATE';
  readonly protocol: 1;
  readonly requestId: string;
  readonly clientId: string;
  readonly expiresAt: number;
}

function record(value: unknown): value is Record<string, unknown> { return Boolean(value) && typeof value === 'object'; }
function currentDeadline(expiresAt: number): boolean { return Number.isFinite(expiresAt) && expiresAt > Date.now() && expiresAt <= Date.now() + PWA_UPDATE_MAX_DURATION_MS; }
function beforeDeadline<T>(value: Promise<T>, expiresAt: number): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Update coordination expired')), Math.max(0, expiresAt - Date.now()));
    value.then((result) => { clearTimeout(timer); resolve(result); }, (error: unknown) => { clearTimeout(timer); reject(error); });
  });
}
function request(value: unknown): value is CoordinationRequest {
  return record(value) && value.type === 'EVENT_UPDATE_COORDINATE' && value.protocol === PWA_UPDATE_PROTOCOL
    && typeof value.requestId === 'string' && /^[A-Za-z0-9-]{1,80}$/.test(value.requestId)
    && typeof value.clientId === 'string' && /^[A-Za-z0-9-]{1,160}$/.test(value.clientId)
    && typeof value.expiresAt === 'number' && currentDeadline(value.expiresAt);
}

/** Direct worker→window port only; no BroadcastChannel, account, QR or roster.
 * A reserved window checks fresh outbox metadata in both phases. Old documents
 * without this handler cannot be declared idle by the waiting worker. */
export function attachPwaUpdateCoordinator(
  container: ServiceWorkerContainer,
  inspectOutbox: () => Promise<boolean>,
): () => void {
  let alive = true;
  let preparing = false;
  let preparationId = 0;
  let cleanup: (() => void) | undefined;
  const receive = (event: MessageEvent<unknown>) => {
    if (!record(event.data) || event.data.type !== 'EVENT_UPDATE_COORDINATE') return;
    const port = event.ports[0];
    if (!port || !request(event.data) || event.origin !== window.location.origin || preparing || cleanup) return;
    const data = event.data;
    preparing = true;
    const currentPreparation = ++preparationId;
    let ownedRelease: (() => void) | undefined;
    void (async () => {
      const registration = await beforeDeadline(container.getRegistration('/'), data.expiresAt);
      const worker = registration?.waiting;
      if (!alive || currentPreparation !== preparationId || !worker || event.source !== worker || worker.state !== 'installed'
        || new URL(worker.scriptURL).origin !== window.location.origin
        || new URL(worker.scriptURL).pathname !== '/event-sw.js' || !currentDeadline(data.expiresAt)) return;
      let closed = false;
      let commitStarted = false;
      const reply = (phase: 'prepare' | 'commit' | 'revoked', readiness: UpdateReadiness) => {
        if (!closed) try { port.postMessage({ type: 'EVENT_UPDATE_READINESS', protocol: PWA_UPDATE_PROTOCOL,
          requestId: data.requestId, clientId: data.clientId, phase, readiness }); } catch { /* Worker disappeared; bounded lease still releases. */ }
      };
      const reservation = reservePwaUpdate(data.expiresAt, () => reply('revoked', 'busy'));
      if (!reservation) { reply('prepare', 'busy'); port.close(); return; }
      const workerStateChanged = () => {
        if (worker.state === 'activated' || worker.state === 'redundant') release();
      };
      const release = () => {
        if (closed) return;
        closed = true;
        clearTimeout(timer);
        worker.removeEventListener('statechange', workerStateChanged);
        port.onmessage = null;
        port.onmessageerror = null;
        port.close();
        reservation.release();
        if (currentPreparation === preparationId) preparing = false;
        if (cleanup === release) cleanup = undefined;
      };
      const timer = setTimeout(release, data.expiresAt - Date.now());
      cleanup = release;
      ownedRelease = release;
      worker.addEventListener('statechange', workerStateChanged);
      const check = async (): Promise<UpdateReadiness> => {
        try {
          // The legacy vault helper treats absent IndexedDB as no captures.
          // Coordination instead requires an inspectable durable journal.
          if (!globalThis.indexedDB || typeof globalThis.indexedDB.open !== 'function') return 'unverified';
          if (!reservation.valid() || !canApplyPwaUpdate()) return 'busy';
          const pending = await beforeDeadline(inspectOutbox(), data.expiresAt);
          if (!alive || !currentDeadline(data.expiresAt)) return 'unverified';
          return !pending && reservation.valid() && canApplyPwaUpdate() ? 'ready' : 'busy';
        } catch { return 'unverified'; }
      };
      port.onmessage = (message: MessageEvent<unknown>) => {
        const value = message.data;
        if (!record(value) || value.protocol !== PWA_UPDATE_PROTOCOL || value.requestId !== data.requestId
          || value.clientId !== data.clientId || value.expiresAt !== data.expiresAt || !currentDeadline(data.expiresAt)) {
          reply('revoked', 'unverified'); release(); return;
        }
        if (value.type === 'EVENT_UPDATE_RELEASE') {
          // skipWaiting resolving is not itself proof of an activated worker.
          // Keep the local barrier until its state event or bounded expiry.
          if (value.activationRequested === true && worker.state !== 'activated' && worker.state !== 'redundant') return;
          release(); return;
        }
        if (value.type !== 'EVENT_UPDATE_COMMIT' || commitStarted) { reply('revoked', 'unverified'); release(); return; }
        commitStarted = true;
        void check().then((readiness) => { reply('commit', readiness); if (readiness !== 'ready') release(); });
      };
      port.onmessageerror = () => { reply('revoked', 'unverified'); release(); };
      port.start();
      const readiness = await check();
      reply('prepare', readiness);
      if (readiness !== 'ready') release();
    })().catch(() => { ownedRelease?.(); port.close(); }).finally(() => {
      if (currentPreparation === preparationId) preparing = false;
      if (!ownedRelease || cleanup !== ownedRelease) port.close();
    });
  };
  container.addEventListener('message', receive);
  return () => {
    alive = false;
    preparationId++;
    container.removeEventListener('message', receive);
    cleanup?.();
  };
}
